/**
 * 后端枚举归一化层
 *
 * 后端所有枚举均序列化为数字索引（System.Text.Json 默认行为），
 * 前端在 API 层将数字转换为字符串枚举，页面层只接触字符串。
 * 发送请求时再通过 toXxx 转换回数字。
 */

// ==================== 赛事 ====================

export const EVENT_STATUSES = [
  'Draft',
  'RegistrationOpen',
  'RegistrationClosed',
  'InProgress',
  'Completed',
  'Cancelled',
] as const
export type EventStatus = (typeof EVENT_STATUSES)[number]

export const EVENT_STATUS_META: Record<EventStatus, { label: string; color: string }> = {
  Draft: { label: '草稿', color: 'neutral' },
  RegistrationOpen: { label: '报名中', color: 'success' },
  RegistrationClosed: { label: '报名结束', color: 'warning' },
  InProgress: { label: '进行中', color: 'brand' },
  Completed: { label: '已结束', color: 'info' },
  Cancelled: { label: '已取消', color: 'danger' },
}

export const EVENT_TYPES = ['Public', 'University'] as const
export type EventType = (typeof EVENT_TYPES)[number]
export const EVENT_TYPE_META: Record<EventType, { label: string }> = {
  Public: { label: '众创赛' },
  University: { label: '高校赛' },
}

export const REGISTRATION_MODES = ['Team', 'Solo'] as const
export type RegistrationMode = (typeof REGISTRATION_MODES)[number]
export const REGISTRATION_MODE_META: Record<RegistrationMode, { label: string }> = {
  Team: { label: '团队赛' },
  Solo: { label: '个人赛' },
}

export const REGISTRATION_STATUSES = [
  'Pending',
  'Registered',
  'Confirmed',
  'Approved',
  'Cancelled',
  'Rejected',
] as const
export type RegistrationStatus = (typeof REGISTRATION_STATUSES)[number]
export const REGISTRATION_STATUS_META: Record<RegistrationStatus, { label: string; color: string }> = {
  Pending: { label: '待审核', color: 'warning' },
  Registered: { label: '已报名', color: 'info' },
  Confirmed: { label: '已确认', color: 'brand' },
  Approved: { label: '已批准', color: 'success' },
  Cancelled: { label: '已取消', color: 'neutral' },
  Rejected: { label: '已拒绝', color: 'danger' },
}

// ==================== 用户 ====================

export const USER_ROLES = ['None', 'User', 'Admin', 'SuperAdmin'] as const
export type UserRole = (typeof USER_ROLES)[number]
export const USER_ROLE_META: Record<UserRole, { label: string; color: string }> = {
  None: { label: '无角色', color: 'neutral' },
  User: { label: '用户', color: 'info' },
  Admin: { label: '管理员', color: 'brand' },
  SuperAdmin: { label: '超级管理员', color: 'danger' },
}

export const USER_GROUPS = ['Free', 'Standard', 'Premium'] as const
export type UserGroup = (typeof USER_GROUPS)[number]
export const USER_GROUP_META: Record<UserGroup, { label: string; color: string }> = {
  Free: { label: '免费用户', color: 'neutral' },
  Standard: { label: '标准订阅', color: 'info' },
  Premium: { label: '高级订阅', color: 'brand' },
}

// ==================== 玩家 ====================

/**
 * PlayerType: 后端模型 Regulator=1, Survivor=2（0 为空值）
 */
export const PLAYER_TYPES = ['Regulator', 'Survivor'] as const
export type PlayerType = (typeof PLAYER_TYPES)[number]
export const PLAYER_TYPE_META: Record<PlayerType, { label: string }> = {
  Regulator: { label: '监管者' },
  Survivor: { label: '求生者' },
}
export function normalizePlayerType(v: number | null | undefined | string): PlayerType {
  if (typeof v === 'string') return v === 'Regulator' ? 'Regulator' : 'Survivor'
  return v === 1 ? 'Regulator' : 'Survivor'
}
export function toPlayerType(v: PlayerType): number {
  return v === 'Regulator' ? 1 : 2
}

// ==================== 绑定 ====================

export const BINDING_TYPES = ['CaptainInvite', 'TransferRequest'] as const
export type BindingType = (typeof BINDING_TYPES)[number]
export const BINDING_TYPE_META: Record<BindingType, { label: string }> = {
  CaptainInvite: { label: '队长邀请' },
  TransferRequest: { label: '转会申请' },
}

export const BINDING_STATUSES = ['Pending', 'Accepted', 'Rejected', 'Expired', 'Cancelled'] as const
export type BindingStatus = (typeof BINDING_STATUSES)[number]
export const BINDING_STATUS_META: Record<BindingStatus, { label: string; color: string }> = {
  Pending: { label: '待处理', color: 'warning' },
  Accepted: { label: '已接受', color: 'success' },
  Rejected: { label: '已拒绝', color: 'danger' },
  Expired: { label: '已过期', color: 'neutral' },
  Cancelled: { label: '已取消', color: 'neutral' },
}

// ==================== 通用转换器 ====================

/** 从 const 数组生成 normalize / toNumber 函数对 */
function enumConverter<T extends readonly string[]>(arr: T) {
  const map = new Map(arr.map((v, i) => [i, v]))
  const reverse = new Map(arr.map((v, i) => [v, i]))
  return {
    normalize: (v: number | string | null | undefined): T[number] | undefined => {
      if (v === null || v === undefined) return undefined
      if (typeof v === 'string') return (arr as readonly string[]).includes(v) ? (v as T[number]) : undefined
      return map.get(v) as T[number] | undefined
    },
    toNumber: (v: T[number] | string | undefined | null): number | undefined => {
      if (v === undefined || v === null) return undefined
      return reverse.get(v)
    },
  }
}

export const eventStatusC = enumConverter(EVENT_STATUSES)
export const eventTypeC = enumConverter(EVENT_TYPES)
export const registrationModeC = enumConverter(REGISTRATION_MODES)
export const registrationStatusC = enumConverter(REGISTRATION_STATUSES)
export const userRoleC = enumConverter(USER_ROLES)
export const userGroupC = enumConverter(USER_GROUPS)
export const bindingTypeC = enumConverter(BINDING_TYPES)
export const bindingStatusC = enumConverter(BINDING_STATUSES)

export const normalizeEventStatus = eventStatusC.normalize
export const toEventStatus = eventStatusC.toNumber
export const normalizeEventType = eventTypeC.normalize
export const toEventType = eventTypeC.toNumber
export const normalizeRegistrationMode = registrationModeC.normalize
export const toRegistrationMode = registrationModeC.toNumber
export const normalizeRegistrationStatus = registrationStatusC.normalize
export const toRegistrationStatus = registrationStatusC.toNumber
export const normalizeUserRole = userRoleC.normalize
export const toUserRole = userRoleC.toNumber
export const normalizeUserGroup = userGroupC.normalize
export const toUserGroup = userGroupC.toNumber
export const normalizeBindingType = bindingTypeC.normalize
export const toBindingType = bindingTypeC.toNumber
export const normalizeBindingStatus = bindingStatusC.normalize
export const toBindingStatus = bindingStatusC.toNumber
